import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';

const ContactPage: React.FC = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { showToast } = useToast();

  const translations: Record<string, any> = {
    en: {
      title: 'Contact', heading_pre: 'Write to', heading_em: 'Mr.Mirror', heading_post: '.',
      subtitle: "We'd love to hear from you. Send a message and we'll respond as soon as we can.",
      name: 'Name', email: 'Email', subject: 'Subject', message: 'Message',
      send: 'Send', sending: 'Sending…',
      success: 'Message sent. We will be in touch.',
      error: 'Failed to send message. Please try again.',
      nameRequired: 'Name is required', emailRequired: 'Email is required',
      emailInvalid: 'Please enter a valid email', subjectRequired: 'Subject is required', messageRequired: 'Message is required',
    },
    tr: {
      title: 'İLETİŞİM', heading_pre: '', heading_em: 'Bize yaz', heading_post: '.',
      subtitle: 'Bir mesaj bırak, en kısa sürede dönüş yapalım. Doğru kişi seni bekler.',
      name: 'Ad Soyad', email: 'E-posta', subject: 'Konu', message: 'Mesaj',
      send: 'Mesaj Gönder', sending: 'Gönderiliyor…',
      success: 'Mesajın iletildi. Yakında dönüş yapacağız.',
      error: 'Mesaj gönderilemedi. Lütfen tekrar dene.',
      nameRequired: 'Ad soyad gerekli', emailRequired: 'E-posta gerekli',
      emailInvalid: 'Geçerli bir e-posta gir', subjectRequired: 'Konu gerekli', messageRequired: 'Mesaj gerekli',
    },
  };

  const trans = translations[language] || translations.en;

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = trans.nameRequired;
    if (!formData.email.trim()) errors.email = trans.emailRequired;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = trans.emailInvalid;
    if (!formData.subject.trim()) errors.subject = trans.subjectRequired;
    if (!formData.message.trim()) errors.message = trans.messageRequired;
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    try {
      await axios.post('/api/contact', formData);
      showToast(trans.success, 'success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setFieldErrors({});
    } catch (err: any) {
      console.error('Contact form error:', err);
      showToast(trans.error, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-5xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24 w-full">
        <div className="flex items-baseline justify-between mb-12">
          <span className="eyebrow"><span className="text-accent">/01</span> · {trans.title}</span>
          <span className="eyebrow-mute hidden md:inline">CH. 01</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Left side — heading + meta */}
          <div className="md:col-span-5">
            <h1 className="display-h display-lg mb-6">
              {trans.heading_pre} <em>{trans.heading_em}</em>{trans.heading_post}
            </h1>
            <p className="font-prose text-[16px] leading-relaxed text-ink-dim mb-12 max-w-md">
              {trans.subtitle}
            </p>

            <div className="space-y-6 border-t border-hairline pt-8">
              <div>
                <span className="eyebrow-mute mb-1.5 block">E-MAIL</span>
                <a href="mailto:info@mrmirror.ai" className="font-prose text-[16px] text-ink hover:text-accent transition-colors">
                  info@mrmirror.ai
                </a>
              </div>
              <div>
                <span className="eyebrow-mute mb-1.5 block">{language === 'tr' ? 'YANIT SÜRESİ' : 'RESPONSE TIME'}</span>
                <span className="font-prose text-[16px] text-ink">
                  {language === 'tr' ? '24 saat içinde' : 'Within 24 hours'}
                </span>
              </div>
            </div>
          </div>

          {/* Right side — form */}
          <form onSubmit={handleSubmit} noValidate className="md:col-span-7 space-y-8 md:border-l md:border-hairline md:pl-12">
            {([
              { id: 'name', label: trans.name, type: 'text' },
              { id: 'email', label: trans.email, type: 'email' },
              { id: 'subject', label: trans.subject, type: 'text' },
            ] as const).map(({ id, label, type }) => (
              <div key={id}>
                <label htmlFor={id} className="field-label">{label}</label>
                <input
                  type={type}
                  id={id}
                  value={formData[id]}
                  onChange={(e) => {
                    setFormData({ ...formData, [id]: e.target.value });
                    if (fieldErrors[id]) setFieldErrors((fe) => ({ ...fe, [id]: '' }));
                  }}
                  aria-invalid={!!fieldErrors[id]}
                  aria-describedby={fieldErrors[id] ? `${id}-error` : undefined}
                  className={`input-cinematic ${fieldErrors[id] ? '!border-b-signal-red' : ''}`}
                />
                {fieldErrors[id] && (
                  <p id={`${id}-error`} className="mt-2 font-mono text-[10px] tracking-wider uppercase text-signal-red">
                    {fieldErrors[id]}
                  </p>
                )}
              </div>
            ))}

            <div>
              <label htmlFor="message" className="field-label">{trans.message}</label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => {
                  setFormData({ ...formData, message: e.target.value });
                  if (fieldErrors.message) setFieldErrors((fe) => ({ ...fe, message: '' }));
                }}
                rows={5}
                aria-invalid={!!fieldErrors.message}
                aria-describedby={fieldErrors.message ? 'message-error' : undefined}
                className={`input-cinematic resize-none ${fieldErrors.message ? '!border-b-signal-red' : ''}`}
              />
              {fieldErrors.message && (
                <p id="message-error" className="mt-2 font-mono text-[10px] tracking-wider uppercase text-signal-red">
                  {fieldErrors.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-accent w-full justify-center !py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? trans.sending : trans.send}
              {!loading && <span aria-hidden>→</span>}
            </button>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
